import { Link } from '@tanstack/react-router';
import { ArrowUpRight, Minus, Plus } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@worldcoo/ui/components/accordion';
import { NGO_APPLICATION_FORM_URL } from './external-links';

export type FaqItem = {
	question: string;
	answer: string;
	bullets?: string[];
	/** After the list. */
	outro?: string;
	link?: { label: string; target: FaqLinkTarget };
};

/** Closed, as the items come from a JSON file: a raw path would let a translator invent a route. */
export type FaqLinkTarget = 'selection-criteria' | 'ngo-signup' | 'contact' | 'contact-donor' | 'privacy-policy';

/** Inlined, so it takes the list's colour. */
function HeartBullet() {
	return (
		<svg viewBox="0 0 8 8" className="mt-[0.45em] size-2 shrink-0" fill="currentColor" aria-hidden>
			<path d="M4 7.2C3.8 7.2 3.6 7.13 3.45 7L1.2 5.2C0.45 4.6 0 3.7 0 2.75 0 1.23 1.1 0 2.45 0c.6 0 1.15.24 1.55.63C4.4.24 4.95 0 5.55 0 6.9 0 8 1.23 8 2.75c0 .95-.45 1.85-1.2 2.45L4.55 7c-.15.13-.35.2-.55.2Z" />
		</svg>
	);
}

/** Internal links go through the router, so the slug is rewritten per locale. */
function AnswerLink({ link }: { link: NonNullable<FaqItem['link']> }) {
	const className = 'text-brand-strong mt-4 inline-flex items-center gap-1 font-medium hover:underline';
	const label = (
		<>
			{link.label}
			<ArrowUpRight className="size-4" />
		</>
	);

	// The sign-up form the footer's "NGO sign-up" opens, so both start an NGO in the same place.
	if (link.target === 'ngo-signup') {
		return (
			<a href={NGO_APPLICATION_FORM_URL} target="_blank" rel="noopener noreferrer" className={className}>
				{label}
			</a>
		);
	}
	// Not the chooser: the audience decides the category a refund question is filed under.
	if (link.target === 'contact-donor') {
		return (
			<Link to="/www/contact/$audience" params={{ audience: 'donor' }} className={className}>
				{label}
			</Link>
		);
	}
	// The two above are excluded: one leaves the site and one carries route params.
	const ROUTES = {
		'contact': '/www/contact',
		'selection-criteria': '/www/selection-criteria',
		'privacy-policy': '/www/privacy-policy',
	} as const satisfies Record<Exclude<FaqLinkTarget, 'ngo-signup' | 'contact-donor'>, string>;

	return (
		<Link to={ROUTES[link.target]} className={className}>
			{label}
		</Link>
	);
}

/**
 * The plus and MINUS are two glyphs, so the Accordion's chevrons are HIDDEN. Its `ml-auto` rule targets
 * them too, hence `flex-1` on the question, or the tile sits against the text.
 */
export function FaqAccordion({ items }: { items: FaqItem[] }) {
	return (
		<Accordion type="single" collapsible className="flex w-full max-w-[842px] flex-col gap-2">
			{items.map((item) => (
				<AccordionItem
					key={item.question}
					value={item.question}
					className="hover:bg-sand data-[state=open]:bg-sand rounded-2xl border-none px-6 transition-colors lg:px-8"
				>
					<AccordionTrigger className="items-center gap-6 py-6 text-left text-lg leading-[1.3] font-medium tracking-[-0.02em] text-black hover:no-underline lg:py-8 lg:text-xl [&_[data-slot=accordion-trigger-icon]]:hidden">
						<span className="flex-1">{item.question}</span>
						<span className="bg-brand grid size-8 shrink-0 place-items-center rounded-lg text-white">
							<Plus className="size-4 group-aria-expanded/accordion-trigger:hidden" />
							<Minus className="hidden size-4 group-aria-expanded/accordion-trigger:block" />
						</span>
					</AccordionTrigger>
					<AccordionContent className="pb-6 lg:pb-8">
						<p className="text-ink/80 text-base leading-[1.5]">{item.answer}</p>
						{item.bullets && (
							<ul className="mt-4 flex flex-col gap-2">
								{item.bullets.map((bullet) => (
									<li key={bullet} className="text-ink flex items-start gap-2 text-base leading-[1.5]">
										<HeartBullet />
										{bullet}
									</li>
								))}
							</ul>
						)}
						{item.outro && <p className="text-ink/80 mt-4 text-base leading-[1.5]">{item.outro}</p>}
						{item.link && <AnswerLink link={item.link} />}
					</AccordionContent>
				</AccordionItem>
			))}
		</Accordion>
	);
}
